<script setup lang="ts">
  import { toTypedSchema } from '@vee-validate/zod';
  import { useDebounceFn } from '@vueuse/core';
  import { useForm } from 'vee-validate';
  import { ref, watch } from 'vue';
  import { onBeforeRouteLeave } from 'vue-router';
  import * as zod from 'zod';

  import type {
    SMSTemplateSchema,
    SMSTemplateVariable,
    ValidateTemplateResponse,
  } from '@stuut/api-sdk/models';
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Button } from '@ui/components/button';
  import { ConfirmDialog } from '@ui/components/confirm-dialog';
  import { FormControl, FormField, FormItem, FormLabel } from '@ui/components/form';
  import { Input } from '@ui/components/input';
  import type { SaveStatus } from '@ui/components/save-status';
  import { Separator } from '@ui/components/separator';
  import { toast } from '@ui/components/sonner';
  import { Switch } from '@ui/components/switch';
  import type { Editor } from '@ui/components/text-editor';
  import { TextEditor } from '@ui/components/text-editor';

  import { sdk } from '@/sdk';

  import SmsTemplateVariableSelect from './SmsTemplateVariableSelect.vue';

  interface Props {
    hideSaveStatus?: boolean;
    readonly?: boolean;
    template: SMSTemplateSchema;
  }

  const props = withDefaults(defineProps<Props>(), {
    hideSaveStatus: false,
    readonly: false,
  });
  const emits = defineEmits<{
    (e: 'deleted'): void;
    (e: 'default'): void;
    (e: 'updated', template: SMSTemplateSchema): void;
    (e: 'saveStatus', status: SaveStatus): void;
    (e: 'validation', validation: ValidateTemplateResponse | null): void;
    (e: 'isValidating', isValidating: boolean): void;
  }>();

  const saveStatus = ref<SaveStatus>('saved');
  const isDeleting = ref(false);
  const bodyEditor = ref<Editor | null>(null);
  const unSaveChangesDialogOpen = ref(false);
  const unSaveChanges = ref<boolean | null>(null);
  const validation = ref<ValidateTemplateResponse | null>(null);
  const isValidating = ref(false);

  // Convert plain text with Jinja {{ variable }} to HTML with styled spans
  const jinjaToHtml = (text: string): string => {
    if (!text) return '';
    const html = text
      .replace(/\{\{\s*(\w+)\s*\}\}/g, (_, varName) => {
        return `<span class="jinja-variable border border-white select-none rounded font-mono text-primary text-xs! p-1 whitespace-nowrap!" variable-name="${varName}" label="${varName}">${varName}</span>`;
      })
      .replace(/\n/g, '<br>');
    return `<div>${html}</div>`;
  };

  // Convert HTML with styled spans back to plain text with Jinja {{ variable }}
  const htmlToJinja = (html: string): string => {
    if (!html) return '';
    // Create a temporary div to parse HTML
    const div = document.createElement('div');
    div.innerHTML = html;

    // Replace jinja-variable spans with {{ variable_name }}
    const jinjaSpans = div.querySelectorAll('span.jinja-variable');
    jinjaSpans.forEach((span) => {
      const varName = span.getAttribute('variable-name');
      if (varName) {
        const textNode = document.createTextNode(`{{ ${varName} }}`);
        span.parentNode?.replaceChild(textNode, span);
      }
    });

    // Get text content, preserving line breaks
    let text = '';
    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        if (el.tagName === 'BR') {
          text += '\n';
        } else if (el.tagName === 'DIV' || el.tagName === 'P') {
          if (text && !text.endsWith('\n')) {
            text += '\n';
          }
          el.childNodes.forEach(walk);
        } else {
          el.childNodes.forEach(walk);
        }
      }
    };
    walk(div);

    return text.trim();
  };

  const initialBodyHtml = jinjaToHtml(props.template.body || '');

  const formData = useForm({
    initialValues: {
      body: initialBodyHtml,
      description: props.template.description ?? '',
      isDefault: props.template.isDefault,
      name: props.template.name ?? 'New SMS Template',
    },
    validationSchema: toTypedSchema(
      zod.object({
        body: zod.string().min(1, 'Body is required'),
        description: zod.string().optional(),
        isDefault: zod.boolean().default(false),
        name: zod.string().min(1, 'Name is required'),
      }),
    ),
  });

  const onBodyEditorReady = (e: Editor) => {
    bodyEditor.value = e;
  };

  const deleteTemplate = () => {
    if (isDeleting.value) return;

    isDeleting.value = true;

    return sdk.smsTemplates
      .delete({ smsTemplateId: props.template.id })
      .then(() => {
        toast.success('SMS Template has been deleted');
        emits('deleted');
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to delete template');
      })
      .finally(() => {
        isDeleting.value = false;
      });
  };

  const confirmNavigation = () => {
    return new Promise((resolve) => {
      unSaveChangesDialogOpen.value = true;

      const stop = watch(unSaveChangesDialogOpen, (isOpen) => {
        if (!isOpen) {
          stop();
          resolve(unSaveChanges.value);
        }
      });
    });
  };

  onBeforeRouteLeave(async (to, from, next) => {
    if (saveStatus.value === 'saving') {
      const userConfirmed = await confirmNavigation();
      if (userConfirmed) {
        next();
      } else {
        next(false);
        debouncedUpdate();
      }
    } else {
      next();
    }
  });

  const validateBody = async (plainTextBody: string) => {
    if (!plainTextBody) {
      validation.value = null;
      emits('validation', null);
      return;
    }

    isValidating.value = true;
    emits('isValidating', true);

    try {
      const result = await sdk.smsTemplates.validate({ body: plainTextBody });
      validation.value = result;
      emits('validation', result);
    } catch (err) {
      console.error('Validation error:', err);
      validation.value = null;
      emits('validation', null);
    } finally {
      isValidating.value = false;
      emits('isValidating', false);
    }
  };

  const save = async () => {
    if (props.readonly) return;
    // Convert HTML body to plain text with Jinja syntax
    const plainTextBody = htmlToJinja(formData.values.body || '');

    const templateBody = {
      body: plainTextBody,
      description: formData.values.description || null,
      isDefault: formData.values.isDefault,
      name: formData.values.name,
    };

    // 1. Form validation (name/body not empty)
    const validationResult = await formData.validate();
    if (!validationResult.valid) {
      saveStatus.value = 'dirty';
      return;
    }

    // 2. Validate body BEFORE save
    if (plainTextBody) {
      isValidating.value = true;
      emits('isValidating', true);
      try {
        const bodyValidation = await sdk.smsTemplates.validate({ body: plainTextBody });
        validation.value = bodyValidation;
        emits('validation', bodyValidation);

        if (!bodyValidation.valid) {
          saveStatus.value = 'dirty';
          return; // Block save if validation fails
        }
      } catch (err) {
        console.error('Validation error:', err);
        // Allow save if validation API fails
      } finally {
        isValidating.value = false;
        emits('isValidating', false);
      }
    }

    // 3. Save template
    try {
      const updatedTemplate = await sdk.smsTemplates.update({
        body: templateBody,
        smsTemplateId: props.template.id,
      });

      saveStatus.value = 'saved';

      if (updatedTemplate.isDefault) {
        emits('default');
      }
      emits('updated', updatedTemplate);
    } catch (err) {
      console.error(err);
      saveStatus.value = 'dirty';
      toast.error('Failed to save template');
    }
  };

  const debouncedUpdate = useDebounceFn(() => {
    save();
  }, 500);

  watch(
    () => formData.values,
    () => {
      if (props.readonly) return;
      saveStatus.value = 'saving';
      debouncedUpdate();
    },
    { deep: true },
  );

  // Initial validation
  if (props.template.body) {
    validateBody(props.template.body);
  }

  const insertTemplateVariable = (variable: SMSTemplateVariable) => {
    if (!bodyEditor.value) return;

    bodyEditor.value
      .chain()
      .focus()
      .addJinjaVariable({
        label: variable.displayName,
        'variable-name': variable.name,
      })
      .run();
  };

  const handleUserDecision = (decision: boolean) => {
    unSaveChanges.value = decision;
    unSaveChangesDialogOpen.value = false;
  };

  watch(saveStatus, () => emits('saveStatus', saveStatus.value), {
    immediate: true,
  });
</script>

<template>
  <div class="h-full flex flex-col">
    <form class="flex flex-col h-full" @keydown.enter="(e) => e.preventDefault()">
      <div class="flex flex-col px-4 gap-4 py-4">
        <FormField v-slot="{ componentField }" name="name">
          <FormItem v-auto-animate layout="horizontal">
            <FormLabel class="w-[80px]">Name</FormLabel>
            <FormControl>
              <Input v-bind="componentField" placeholder="Template name" :disabled="readonly" />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="description">
          <FormItem v-auto-animate layout="horizontal">
            <FormLabel class="w-[80px]">Description</FormLabel>
            <FormControl>
              <Input
                v-bind="componentField"
                placeholder="Optional description"
                :disabled="readonly"
              />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField v-slot="{ value, handleChange }" name="isDefault">
          <FormItem v-auto-animate layout="horizontal">
            <FormLabel class="w-[80px]">Default</FormLabel>
            <FormControl>
              <Switch
                :model-value="value"
                :disabled="readonly"
                @update:model-value="handleChange"
              />
            </FormControl>
          </FormItem>
        </FormField>
      </div>

      <Separator />

      <div class="flex-1 flex flex-col p-4 gap-2 min-h-0 overflow-hidden">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium">Message Body</span>
          <SmsTemplateVariableSelect @variable-selected="insertTemplateVariable" />
        </div>

        <FormField name="body">
          <FormItem class="flex-1 flex flex-col min-h-0 overflow-hidden">
            <FormControl>
              <div class="flex-1 border rounded-sm overflow-y-auto p-2 min-h-[200px]">
                <TextEditor
                  :model-value="formData.values.body"
                  placeholder-text="Enter your SMS message here. Use the Variable button to insert dynamic content."
                  disable-inline-images
                  :disabled="readonly"
                  :disable-editing="readonly"
                  @update:model-value="(value) => formData.setFieldValue('body', value ?? '')"
                  @ready="onBodyEditorReady"
                />
              </div>
            </FormControl>
          </FormItem>
        </FormField>
      </div>

      <Separator />

      <div class="flex flex-row justify-between items-center px-4 py-3">
        <ConfirmDialog confirm-text="Delete" @confirm="deleteTemplate">
          <template #title> Are you sure?</template>

          <template #description>
            This will permanently delete this SMS template. This action cannot be undone.
          </template>

          <template #trigger>
            <Button
              variant="ghost"
              size="icon"
              type="button"
              class="text-destructive"
              :disabled="isDeleting || readonly"
            >
              <i v-if="isDeleting" class="fa fa-spinner-third animate-spin text-base" />
              <i v-else class="fa far fa-trash-can" />
            </Button>
          </template>
        </ConfirmDialog>

        <div v-if="!hideSaveStatus" class="flex items-center gap-2 text-xs text-muted-foreground">
          <i v-if="saveStatus === 'saving'" class="fa fa-spinner-third animate-spin text-sm" />
          <span v-else>Saved</span>
        </div>
      </div>
    </form>

    <AlertDialog v-model:open="unSaveChangesDialogOpen">
      <AlertDialogContent v-if="unSaveChangesDialogOpen">
        <AlertDialogHeader>
          <AlertDialogTitle> Are you sure?</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription>
          Your changes are still saving. Are you sure you want to leave?
        </AlertDialogDescription>
        <AlertDialogFooter>
          <Button @click="handleUserDecision(false)">Cancel</Button>
          <Button variant="outline" @click="handleUserDecision(true)">Confirm</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
